import { comparePdfLabels, pdfHeaderText, pdfHeaderTag } from "./pdfTagName";
import type { State } from "../State";
import type Consumer from "../models/Consumer";
import type Group from "../models/Group";
import type C1_EloItem from "../../ConceptOne/src/model/C1_EloItem";
import type Device from "../models/Device";
import type { C1_Consumer } from "../../ConceptOne/src/model/C1_Consumer";
import { ChannelType, channelTypeInfo, isPlugChannelType, isSwitchedPlugChannelType, isCablelessPlugChannelType } from "../models/ChannelType";

export type BillOfMaterialsOptions = {
  mode: "total" | "floors";
  usage: boolean;
  distinguishLightTypes: boolean;
  includeWithoutProducts?: boolean;
};
export type MaterialUsage = { tag: string; floor: string; room: string; name: string };
export type MaterialPosition = {
  section: string; group: string; specification: string; count: number; usages: MaterialUsage[];
  /** Distinct templates must not merge merely because their display names match. */
  productId?: string;
  withoutProduct?: boolean;
};
export type MaterialScope = { title: string; positions: MaterialPosition[]; isTotal?: boolean };
export const materialSectionOrder = [
  "Leuchten", "Taster", "Sensoren", "Dezentrale Aktoren", "Beschattung", "Heizung",
  "Steckdosen", "Festanschlüsse", "Schaltschrankgeräte", "Klemmen", "Sonstiges",
];

export const materialChapterOrder = [
  "Schaltschrank-Material (Aktoren)", "Klemmen", "Taster", "Kontakte", "Leuchten",
  "Beschattung", "Heizen", "Sensoren",
  "Steckdosen", "Festanschlüsse", "Sonstige",
];
export function materialChapter(section: string): string {
  if (section === "Schaltschrankgeräte") return "Schaltschrank-Material (Aktoren)";
  if (section === "Klemmen") return section;
  if (section === "Heizung") return "Heizen";
  if (section === "Fensterkontakte") return "Kontakte";
  if (["Taster", "Leuchten", "Beschattung", "Sensoren"].includes(section)) return section;
  if (["Steckdosen", "Festanschlüsse"].includes(section)) return section;
  return "Sonstige";
}

/** Aggregates physical objects, never their channels, connections or C1 definition trees. */
function aggregate(positions: MaterialPosition[]): MaterialPosition[] {
  const result = new Map<string, MaterialPosition>();
  for (const position of positions) {
    const key = JSON.stringify([position.section, position.group, position.specification, position.productId]);
    const current = result.get(key);
    if (current) {
      current.count += position.count;
      current.usages.push(...position.usages);
    } else result.set(key, { ...position, usages: [...position.usages] });
  }
  const sectionIndex = (section: string) => {
    const index = materialSectionOrder.indexOf(section);
    return index < 0 ? materialSectionOrder.length : index;
  };
  return [...result.values()].sort((a, b) =>
    sectionIndex(a.section) - sectionIndex(b.section) ||
    a.section.localeCompare(b.section, "de") ||
    a.group.localeCompare(b.group, "de", { numeric: true }) ||
    a.specification.localeCompare(b.specification, "de", { numeric: true }));
}

/** Undefined uses the native classification; null denotes an internal product connection. */
function eloMaterialPosition(app: State, facet: C1_EloItem | undefined, usage: MaterialUsage, count: number): MaterialPosition | null | undefined {
  const material = facet?.material;
  if (!material) return undefined;
  if (material.role === "connection") return null;
  const name = material.name[app.settings.language.startsWith("en") ? "en" : "de"];
  return { section: material.category, group: name, specification: name,
    productId: `elo:${material.productId}`, count, usages: [usage] };
}

/** Unassigned devices remain recognizable and can be excluded from the document. */
function devicePosition(device: Device, section: string, usage: MaterialUsage): MaterialPosition {
  const specification = device.template?.name || "Ohne Produktzuordnung";
  return { section, group: section, specification, productId: device.template?.id,
    withoutProduct: !device.template, count: 1, usages: [usage] };
}

/** Classifies native consumers; optional C1 facets enrich their construction specification. */
function consumerPosition(app: State, consumer: Consumer, usage: MaterialUsage, distinguish: boolean): MaterialPosition {
  const info = channelTypeInfo(consumer.channelType);
  const facet = consumer.c1?.className === "C1_Consumer" ? consumer.c1 as C1_Consumer : undefined;
  const details = facet?.data.details;
  let section = "Sonstiges";
  let group = consumer.channelType === ChannelType.suply230V
    ? "230V" : consumer.channelType === ChannelType.suply400V
      ? "400V" : app.translate(`channelType.${consumer.channelType}`, consumer.channelType);
  let specification = group;
  if (isPlugChannelType(consumer.channelType)) {
    section = "Steckdosen";
    specification = app.translate(`channelType.${consumer.channelType}`, consumer.channelType);
    group = info.isLan ? "Netzwerk" : isCablelessPlugChannelType(consumer.channelType) ? "Multimedia"
      : isSwitchedPlugChannelType(consumer.channelType) ? "Geschaltet" : "Dauerstrom";
  } else if (info.isSupply) {
    section = "Festanschlüsse";
  } else if (info.isLight) {
    section = "Leuchten";
    if (consumer.isDali) group = `DALI · ${group}`;
    specification = distinguish
      ? facet?.lightProductDefinition?.descriptionShort[app.settings.language.startsWith("en") ? "en" : "de"] || "Ohne Bauart"
      : group;
  } else if (consumer.channelType === ChannelType.Lock || facet?.definition?.type === "window_contact") {
    section = "Fensterkontakte";
    group = "Fenster";
    specification = "Fenster";
  }
  else if (info.isRollo) section = "Beschattung";
  else if (info.isHeating) {
    section = "Heizung";
    group = "Räume";
    specification = "Räume";
  }
  return { section, group, specification, count: consumer.quantity, usages: [usage] };
}

/** Builds floor scopes and a project total from the live ESTADA model in both applications. */
export function collectBillOfMaterials(app: State, options: BillOfMaterialsOptions): MaterialScope[] {
  const scopes: MaterialScope[] = [];
  const all: MaterialPosition[] = [];
  const seen = new Set<string>();
  const floors = [...app.floors.asArray()].sort((a, b) => comparePdfLabels(a, b));
  for (const floor of floors) {
    if (floor.isDeleted) continue;
    const positions: MaterialPosition[] = [];
    for (const room of floor.rooms.asArray()) {
      if (room.isDeleted) continue;
      const collectGroups = (groups: readonly Group[]) => {
        for (const group of groups) {
          if (group.isDeleted || seen.has(group.id)) continue;
          seen.add(group.id);
          const facet = group.c1?.className === "C1_EloGroup" ? group.c1 as C1_EloItem : undefined;
          const position = eloMaterialPosition(app, facet, {
            tag: pdfHeaderTag(group), floor: floor.name, room: room.name, name: group.name,
          }, facet?.data.count ?? 1);
          if (position) positions.push(position);
          collectGroups(group.groups.asArray());
        }
      };
      collectGroups(room.groups?.asArray() ?? []);
      for (const button of room.allButtons) {
        if (button.isDeleted || seen.has(button.id)) continue;
        seen.add(button.id);
        const section = (button.c1 as C1_EloItem | undefined)?.definition?.type === "window_contact"
          ? "Fensterkontakte" : button.isDecentralActor ? "Dezentrale Aktoren" : button.isSensor ? "Sensoren" : "Taster";
        const usage = {
          tag: pdfHeaderTag(button), floor: floor.name, room: room.name, name: button.name,
        };
        const material = eloMaterialPosition(app, button.c1 as C1_EloItem | undefined, usage, 1);
        if (material !== null) positions.push(material ?? devicePosition(button, section, usage));
      }
      for (const consumer of room.allConsumers()) {
        if (consumer.isDeleted || seen.has(consumer.id)) continue;
        seen.add(consumer.id);
        const usage = {
          tag: pdfHeaderTag(consumer), floor: floor.name, room: room.name, name: consumer.name,
        };
        const material = eloMaterialPosition(app, consumer.c1 as C1_EloItem | undefined, usage, consumer.quantity);
        if (material !== null) positions.push(material ?? consumerPosition(app, consumer, usage, options.distinguishLightTypes));
      }
    }
    const included = positions.filter(position => options.includeWithoutProducts !== false || !position.withoutProduct);
    all.push(...included);
    scopes.push({ title: pdfHeaderText(floor), positions: aggregate(included) });
  }
  const cabinetPositions: MaterialPosition[] = [];
  for (const cabinet of app.cabinets.asArray()) {
    if (cabinet.isDeleted) continue;
    // Same two-storey terminal estimate as the legacy cabinet parts list.
    for (const strip of cabinet.terminalStrips?.asArray() ?? []) {
      if (strip.isDeleted || seen.has(strip.id)) continue;
      seen.add(strip.id);
      let lnpe = 0;
      let ll = 0;
      for (const terminal of strip.terminals.asArray()) {
        if (terminal.isDeleted || !terminal.isFirst) continue;
        const wires = terminal.getTerminalBlock()?.cableType?.numberOfWires;
        if (wires && wires >= 3) {
          lnpe += 1;
          ll += Math.ceil((wires - 3) / 2);
        }
      }
      for (const [specification, count] of [["L/N/PE-Klemmen", lnpe], ["L/L-Klemmen", ll]] as const) {
        if (!count) continue;
        cabinetPositions.push({ section: "Klemmen", group: "Klemmen", specification, count,
          usages: [{ tag: pdfHeaderTag(strip), floor: "", room: cabinet.nameOfDefaultName,
            name: `${strip.nameOfDefaultName || strip.name || "Klemmleiste"} · ${count} Stück` }] });
      }
    }
    for (const actor of cabinet.actors.asArray()) {
      if (actor.isDeleted || seen.has(actor.id)) continue;
      if (channelTypeInfo(actor.channelType).isSupply) continue;
      seen.add(actor.id);
      if (options.includeWithoutProducts === false && !actor.template) continue;
      cabinetPositions.push(devicePosition(actor, "Schaltschrankgeräte", {
        tag: pdfHeaderTag(actor), floor: "", room: cabinet.nameOfDefaultName, name: actor.name,
      }));
    }
  }
  all.push(...cabinetPositions);
  if (cabinetPositions.length) scopes.push({ title: "Schaltschränke", positions: aggregate(cabinetPositions) });
  const total = { title: "Gesamt", positions: aggregate(all), isTotal: true };
  return options.mode === "floors" ? [...scopes, total] : [total];
}
